const API_URL = ''; 

export interface Stage {
  id: string;
  stage: number;
  title: string;
  description: string;
  status: string;
  duration: string;
}

export interface ContactSubmissionData {
  nome: string;
  email: string;
  mensagem: string;
  consentimento: boolean;
}

export interface Document {
  id: string;
  title: string;
  description: string | null;
  filename: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  isPublished: boolean;
  order: number;
  createdAt: string;
}

export interface UploadDocumentData {
  title: string;
  description: string;
  category: string;
  order: number;
  file: File;
}

export interface Report {
  id: string;
  title: string;
  description: string | null;
  filename: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  isPublished: boolean;
  createdAt: string;
}

export interface UploadReportData {
  title: string;
  description: string;
  file: File;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string; 
}

function isTokenValid(token: string): boolean {
  if (!token || token.trim() === '') return false;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    const payload = JSON.parse(atob(parts[1]));
    const expDate = new Date(payload.exp * 1000);
    const now = new Date();
    
    const isValid = expDate > now;
    
    if (!isValid) {
      console.warn('[TOKEN] Token expirado em:', expDate.toLocaleString());
    }
    
    return isValid;
  } catch (error) {
    console.error('[TOKEN] Erro ao validar token:', error);
    return false;
  }
}

function getTokenFromStorage(): string | null {
  const token = localStorage.getItem('token') || 
                sessionStorage.getItem('token') || 
                null;
  
  if (!token) {
    console.warn('[TOKEN] Nenhum token encontrado no storage');
  }
  
  return token;
}

function saveTokenToStorage(token: string): void {
  localStorage.setItem('token', token);
  console.log('[TOKEN] Token salvo no localStorage, primeiros 30 chars:', token.substring(0, 30) + '...');
}

function clearTokenFromStorage(): void {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  console.log('[TOKEN] Token removido do storage');
}

async function authFetch<T>(url: string, token: string, options: RequestInit = {}): Promise<T> {
  console.log('[AUTH_FETCH] Iniciando requisição para:', url);
  console.log('[AUTH_FETCH] Token existe?', !!token);
  
  if (!token) {
    throw new Error('Token não encontrado. Faça login novamente.');
  }
  
  if (!isTokenValid(token)) {
    clearTokenFromStorage();
    throw new Error('Token expirado. Faça login novamente.');
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  });
  
  console.log('[AUTH_FETCH] Response status:', response.status);
  
  if (response.status === 401 || response.status === 403) {
    clearTokenFromStorage();
    throw new Error('Sessão inválida ou expirada. Faça login novamente.');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(errorData.error || `Erro HTTP ${response.status}`);
  }
  
  return response.json();
}

export const api = {
  async getAllStages(): Promise<Stage[]> {
    try {
      console.log('[API] Buscando todas as fases...');
      const response = await fetch(`${API_URL}/api/stages`);
      if (!response.ok) throw new Error('Erro ao buscar fases');
      const data = await response.json();
      console.log('[API] Fases carregadas:', data.length);
      return data;
    } catch (error) {
      console.error('[API] Erro ao carregar fases:', error);
      return [];
    }
  },

  async getCurrentStage(): Promise<{ currentStage: number }> {
    try {
      const response = await fetch(`${API_URL}/api/stages/current`);
      if (!response.ok) throw new Error('Erro ao buscar fase atual');
      return await response.json();
    } catch (error) {
      console.error('[API] Erro ao carregar fase atual:', error);
      return { currentStage: 1 };
    }
  },

  async login(email: string, password: string): Promise<{ token: string; admin: { id: string; email: string; name: string } }> {
    console.log('[API] Tentando login para:', email);
    
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('[API] Erro no login:', error);
      throw new Error(error.error || 'Erro no login');
    }
    
    const data = await response.json();
    console.log('[API] Login bem-sucedido');
    
    saveTokenToStorage(data.token);
    
    return data;
  },

  async logout(token: string): Promise<void> {
    console.log('[API] Realizando logout');
    
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('[API] Erro no logout:', error);
    } finally {
      clearTokenFromStorage();
    }
  },

  async getMe(token: string): Promise<{ id: string; email: string; name: string }> {
    return authFetch(`${API_URL}/api/auth/me`, token);
  },

  async updateStage(token: string, stageId: string, data: { status: string; title?: string; description?: string; duration?: string }): Promise<Stage> {
    return authFetch(`${API_URL}/api/stages/${stageId}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async submitContact(data: ContactSubmissionData): Promise<{ success: boolean; id: string }> {
    console.log('[API] Enviando mensagem de contato...');
    const response = await fetch(`${API_URL}/api/submissions/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao enviar mensagem');
    }
    return response.json();
  },

  async getContactSubmissions(token: string, page: number = 1, limit: number = 50): Promise<{ submissions: any[]; total: number; page: number; limit: number }> {
    return authFetch(`${API_URL}/api/admin/submissions/contact?page=${page}&limit=${limit}`, token);
  },

  async getAdminLogs(token: string, page: number = 1, limit: number = 50): Promise<{ logs: any[]; total: number; page: number; limit: number }> {
    return authFetch(`${API_URL}/api/admin/logs?page=${page}&limit=${limit}`, token);
  },

  async getAdminStats(token: string): Promise<{ submissions: { social: number; ativa: number; contact: number }; stages: any[] }> {
    console.log('[API] Buscando estatísticas...');
    
    const result = await authFetch<{ submissions: { social: number; ativa: number; contact: number }; stages: any[] }>(
      `${API_URL}/api/admin/stats`, 
      token
    );
    
    console.log('[API] Estatísticas obtidas:', result);
    return result;
  },

  async getDocuments(): Promise<Document[]> {
    const response = await fetch(`${API_URL}/api/documents`);
    if (!response.ok) throw new Error('Erro ao buscar documentos');
    return response.json();
  },

  async getAdminDocuments(token: string): Promise<Document[]> {
    return authFetch(`${API_URL}/api/admin/documents`, token);
  },

  async uploadDocument(token: string, data: UploadDocumentData): Promise<{ success: boolean; document: Document }> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    formData.append('order', String(data.order));

    const response = await fetch(`${API_URL}/api/admin/documents`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao fazer upload');
    }
    
    return response.json();
  },

  async updateDocument(token: string, id: string, data: Partial<Document>): Promise<{ success: boolean; document: Document }> {
    return authFetch(`${API_URL}/api/admin/documents/${id}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteDocument(token: string, id: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/documents/${id}`, token, {
      method: 'DELETE'
    });
  },

  async downloadDocument(id: string, _originalName: string): Promise<void> {
    window.open(`${API_URL}/api/documents/${id}/download`, '_blank');
  },

  // ==================== RELATÓRIOS ====================

  async getReport(): Promise<Report | null> {
    const response = await fetch(`${API_URL}/api/reports`);
    if (!response.ok) throw new Error('Erro ao buscar relatório');
    return response.json();
  },

  async getAdminReports(token: string): Promise<Report[]> {
    return authFetch(`${API_URL}/api/admin/reports`, token);
  },

  async uploadReport(token: string, data: UploadReportData): Promise<{ success: boolean; report: Report }> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('title', data.title);
    formData.append('description', data.description);

    const response = await fetch(`${API_URL}/api/admin/reports`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao fazer upload');
    }

    return response.json();
  },

  async updateReport(token: string, id: string, data: Partial<Report>): Promise<{ success: boolean; report: Report }> {
    return authFetch(`${API_URL}/api/admin/reports/${id}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteReport(token: string, id: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/reports/${id}`, token, {
      method: 'DELETE'
    });
  },

  async downloadReport(id: string, _originalName: string): Promise<void> {
    window.open(`${API_URL}/api/reports/${id}/download`, '_blank');
  },

  // ==================== FAQ ====================

  async getFaqs(): Promise<Faq[]> {
    const response = await fetch(`${API_URL}/api/faq`);
    if (!response.ok) throw new Error('Erro ao buscar FAQs');
    return response.json();
  },

  async getAdminFaqs(token: string): Promise<Faq[]> {
    return authFetch(`${API_URL}/api/admin/faq`, token);
  },

  async createFaq(token: string, data: { question: string; answer: string; order: number }): Promise<{ success: boolean; faq: Faq }> {
    return authFetch(`${API_URL}/api/admin/faq`, token, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateFaq(token: string, id: string, data: Partial<Faq>): Promise<{ success: boolean; faq: Faq }> {
    return authFetch(`${API_URL}/api/admin/faq/${id}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteFaq(token: string, id: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/faq/${id}`, token, {
      method: 'DELETE'
    });
  }
};

export { getTokenFromStorage, saveTokenToStorage, clearTokenFromStorage, isTokenValid };